import { useEffect, useState } from "react";
import { GeoJSON } from "react-leaflet";
import { CustomLayerService, GeoService, MapUtility } from "../../utility";
import { message, notification, Spin } from "antd";
import * as ReactDOMServer from "react-dom/server";
import MarkerClusterGroup from "react-leaflet-cluster";
import styled from "styled-components";
import { CommonUtility } from "../../utility/common";
import axios from "axios";
import { useLoading } from "../../hooks/loadingContext";

const Header = styled.div`
  font-weight: bold;
  font-size: 17px;
  padding: 10px 0;
  border-bottom: 1px solid;
  text-align: center;
`;

const SmallHeader = styled.div`
  font-size: 14px;
  padding-bottom: 10px;
  padding-top: 5px;
  text-align: center;
  opacity: 0.7;
`;

const PopupContainer = styled.div`
  min-width: 250px;
  width: 100%;
  max-height: 400px;
  overflow-y: auto;

  p {
    font-size: 90%;
    margin: 0.5rem 0;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
`;

const TableRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #ddd;

  &:nth-child(even) {
    background-color: #f9f9f9;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableData = styled.div`
  width: 50%;
  text-align: left;
  word-wrap: break-word;
  &:first-child {
    font-weight: bold;
  }
`;

const renderValue = (value) => {
  if (typeof value === "object") {
    return "";
  }
  return value;
};

export const Popup = ({ properties, layer }) => {
  return (
    <PopupContainer>
      <div>
        <Header>Name: {properties.name || properties["Store number"]}</Header>
        <SmallHeader>(Layer: {layer?.name})</SmallHeader>
      </div>
      <div>
        <TableContainer>
          {Object.keys(properties).map((key) => (
            <TableRow>
              <TableData>{CommonUtility.toTitleCase(key)}</TableData>
              <TableData>{renderValue(properties[key])}</TableData>
            </TableRow>
          ))}
        </TableContainer>
      </div>
    </PopupContainer>
  );
};

export const LayerRendering = ({ layer, bbox, zoom }) => {
  const [data, setData] = useState(null);
  const { loading, setLoading } = useLoading();
  // const getHeatmapPoints = (geojsonData) => {
  //   if (!geojsonData?.features) return [];
  //   return geojsonData.features.map((feature) => {
  //     const [lng, lat] = feature.geometry.coordinates;
  //     return { point: [lat, lng, 1], ...feature };
  //   });
  // };

  function downloadCSV(data, filename = "data.csv") {
    if (!data || !data.length) {
      console.error("No data available to convert to CSV.");
      return;
    }

    const headers = Object.keys(data[0].properties);
    const geometryHeaders = ["Latitude", "Longitude"]; // Geometry fields if needed separately

    let csvContent = headers.join(",") + "," + geometryHeaders.join(",") + "\n";
    data.forEach((item) => {
      const row = headers
        .map((header) => {
          const value = item.properties[header];
          return typeof value === "string" ? `"${value}"` : value;
        })
        .join(",");

      const geometryRow = `${item.geometry.coordinates[1]},${item.geometry.coordinates[0]}`; // Latitude, Longitude
      csvContent += `${row},${geometryRow}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  useEffect(() => {
    const fetchData = async (cancelToken) => {
      if (layer.isExport) {
        return;
      }
      try {
        if (layer.zoom > zoom) {
          message.warning(
            `Unable to load data for the current zoom level: ${zoom}. Please zoom in to at least level: ${layer.zoom} to view the data.`
          );
          return;
        }
        setData(null);
        setLoading(true);
        const response = await GeoService.layer(
          layer.key,
          {
            bbox: bbox.join(","),
            zoom,
          },
          {
            filters: undefined,
          },
          cancelToken.token
        );
        setLoading(false);
        setData(response);
      } catch (error) {
        notification.error({ message: error.message });
        if (error?.statusCode) {
          setLoading(false);
        }
      }
    };
    if (layer.checked && bbox && !layer.filters?.length) {
      const request = axios.CancelToken.source();
      fetchData(request);

      return () => {
        if (request) {
          request.cancel();
        }
      };
    }
  }, [layer.checked, bbox, zoom, layer.zoom, layer.key, layer.filters]);

  const fetchData1 = async (value, exportdata) => {
    try {
      const data = {
        ...value,
        bbox,
        filters: layer.filters,
        exportdata: exportdata,
      };
      const obj = {
        filters: data?.filters,
      };

      setLoading(true);
      const response = await CustomLayerService.export(data, obj);
      if (exportdata) {
        downloadCSV(response?.features);
        notification.success({ message: "Layer exported successfully!" });
      } else {
        notification.success({ message: "Filter applied! Results updated." });
      }
      setLoading(false);
    } catch (error) {
      if (error?.statusCode) {
        setLoading(false);
        notification.error({
          message:
            "An unexpected error occurred. Please contact support for assistance.",
        });
      }
    }
  };

  useEffect(() => {
    if (layer.isExport) {
      fetchData1(layer, true);
    }
  }, [layer.isExport]);

  useEffect(() => {
    if (layer.checked && layer?.filters?.length > 0 && !layer.isExport) {
      fetchData1(layer, false);
    }
  }, [layer.checked, layer.filters, bbox]);

  const [hoveredFeature, setHoveredFeature] = useState(null);

  const onEachFeature = (feature, layer) => {
    layer.on({
      mouseover: () => {
        setHoveredFeature(feature);
      },
      mouseout: () => {
        setHoveredFeature(null);
      },
      click: () => {
        setHoveredFeature(feature);
        const popupContent = ReactDOMServer.renderToString(
          <Popup properties={feature.properties || {}} layer={layer} />
        );
        layer.bindPopup(popupContent).openPopup();
      },
    });
  };

  const style = (feature) => ({
    weight: hoveredFeature === feature ? 3 : 1.7,
    color: layer.color || "#3388ff",
  });

  const pointToLayer = (_, latlng) => {
    const L = require("leaflet");
    const icon = L.icon({
      iconUrl: MapUtility.getSvgBasedOnColor(layer.color),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
    });
    return L.marker(latlng, { icon });
  };
  return (
    <>
      <Spin spinning={loading} fullscreen />
      {data && (
        <>
          {layer.cluster ? (
            <MarkerClusterGroup chunkedLoading>
              <GeoJSON
                data={data}
                onEachFeature={onEachFeature}
                pointToLayer={pointToLayer}
              />
            </MarkerClusterGroup>
          ) : (
            <GeoJSON data={data} style={style} onEachFeature={onEachFeature} />
          )}
        </>
      )}
    </>
  );
};
